import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  CATEGORY_LABELS_IT,
  CATEGORY_TO_SUBCATEGORIES,
  ParameterSubCategory,
  SUBCATEGORY_LABELS_IT,
} from '../types/ParameterCategories';
import { ParameterData, parameterStatusUtils } from '../types/ParameterData';
import { ParametersTrendCard } from './ParametersTrendCard';

interface Props {
  data: ParameterData[];
}

export const CategoryHealthTrend = ({ data }: Props) => {
  const organizeParameters = () => {
    const organized: Record<string, Record<string, ParameterData[]>> = {};
    const uncategorized: ParameterData[] = [];

    data.forEach((parameter) => {
      const paramName = parameter.name.toUpperCase();
      const { category, subCategory } =
        parameterStatusUtils.getParameterClassification(paramName);

      if (!category) {
        const isDuplicate = uncategorized.some(
          (p) => p.name === parameter.name,
        );
        if (!isDuplicate) {
          uncategorized.push(parameter);
        }
        return;
      }

      if (!organized[category]) {
        organized[category] = {};
      }

      const subCategoryKey = subCategory || 'other';
      if (!organized[category][subCategoryKey]) {
        organized[category][subCategoryKey] = [];
      }

      organized[category][subCategoryKey].push(parameter);
    });

    if (uncategorized.length > 0) {
      organized['uncategorized'] = {
        other: [...new Set(uncategorized)],
      };
    }

    return organized;
  };

  const organizedData = organizeParameters();

  const groupParametersByName = (parameters: ParameterData[]) => {
    const grouped: Record<string, ParameterData[]> = {};
    parameters.forEach((param) => {
      if (!grouped[param.name]) {
        grouped[param.name] = [];
      }
      grouped[param.name].push(param);
    });
    return grouped;
  };

  return (
    <Accordion type='multiple' className='w-full'>
      {Object.entries(CATEGORY_TO_SUBCATEGORIES).map(
        ([category, subcategories]) => {
          const categoryLabel = CATEGORY_LABELS_IT[category];
          const categoryData = organizedData[category];

          if (!categoryData) {
            return null;
          }

          return (
            <AccordionItem key={category} value={category} className='min-h-[78px]'>
              <AccordionTrigger className='h-[78px] text-xl font-semibold'>
                {categoryLabel}
              </AccordionTrigger>
              <AccordionContent>
                <div className='space-y-6'>
                  {subcategories.map((subcategory) => {
                    const parameters = categoryData[subcategory] || [];

                    if (parameters.length === 0) return null;

                    if (subcategory === ParameterSubCategory.Other) {
                      const groupedParameters =
                        groupParametersByName(parameters);
                      return (
                        <div className='space-y-2' key={subcategory}>
                          {Object.entries(groupedParameters).map(
                            ([name, params]) => (
                              <ParametersTrendCard key={name} values={params} />
                            ),
                          )}
                        </div>
                      );
                    }

                    const subCategoryLabel = SUBCATEGORY_LABELS_IT[subcategory];
                    const groupedParameters = groupParametersByName(parameters);
                    return (
                      <div key={subcategory} className='space-y-2'>
                        <h3 className='text-md font-semibold text-gray-700'>
                          {subCategoryLabel}
                        </h3>
                        {Object.entries(groupedParameters).map(
                          ([name, params]) => (
                            <ParametersTrendCard key={name} values={params} />
                          ),
                        )}
                      </div>
                    );
                  })}
                </div>
              </AccordionContent>
              <div className='w-full bg-border h-[1px]'/>
            </AccordionItem>
          );
        },
      )}

      {organizedData['uncategorized'] &&
        organizedData['uncategorized']['other'].length > 0 && (
          <AccordionItem value='uncategorized'>
            <AccordionTrigger className='text-lg font-bold'>
              Altri parametri ({organizedData['uncategorized']['other'].length})
            </AccordionTrigger>
            <AccordionContent>
              <div className='space-y-4'>
                {Object.entries(
                  groupParametersByName(
                    organizedData['uncategorized']['other'],
                  ),
                ).map(([name, params]) => (
                  <ParametersTrendCard key={name} values={params} />
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>
        )}
    </Accordion>
  );
};
